/**
 * `@heidi/runtime/i18n` — the i18n **interface door**: contract + injection,
 * no implementation, zero dependency on `i18next` / `react-i18next`
 * (`i18n-boundary.test.ts` enforces it). The app registers an adapter over its
 * runtime (i18next today, in `src/utils/translations.ts`); package UI and
 * codemodded legacy sites import the contract.
 *
 * ```ts
 * import { useTranslations } from '@heidi/runtime/i18n';
 * const { t } = useTranslations();
 * ```
 *
 * Deprecated by design: this door exists so EXISTING i18next call sites can
 * leave the app alias behind. New strings use react-intl (CLAUDE.md).
 */
export {
  __resetTranslationAdapterForTests,
  hasTranslationAdapter,
  registerTranslationAdapter,
} from './adapter';
export type {
  TranslateFunction,
  TranslationAdapter,
  TranslationHookResult,
  TranslationKey,
  TranslationKeyRegistry,
  TranslationOptions,
} from './contracts';

import { resolveTranslate, resolveTranslationHook } from './adapter';
import { type TranslateFunction, type TranslationHookResult } from './contracts';

/**
 * Returns `{ t, locale }` from the registered adapter, or the documented
 * fallback (the key unchanged, no locale) when none is registered. Named
 * `useTranslations` (plural) to mirror the legacy `@/utils/translations` hook,
 * so moving a call site is a pure specifier rewrite.
 *
 * @deprecated Compatibility door for MOVED legacy i18next call sites only.
 * New strings — in packages and in app code — use react-intl (`useIntl` /
 * `FormattedMessage`, ICU MessageFormat) per CLAUDE.md. The i18n team owns the
 * implementation switch behind this door; when it lands, this hook retires.
 */
export function useTranslations(): TranslationHookResult {
  return resolveTranslationHook();
}

/**
 * Non-React accessor for modules that are not components (the equivalent of a
 * bare `i18next.t(...)`). Resolved per call, so a module that loads before app
 * wiring still sees the adapter once registered.
 *
 * @deprecated Compatibility door for MOVED legacy i18next call sites only; new
 * strings use react-intl (see `useTranslations`).
 */
export const t: TranslateFunction = (key, options) => resolveTranslate()(key, options);
